import { API } from '@shared/types/api';
import { RelatedFilterOptions } from '../types';
import { getRelatedEntityFilterPaths } from './getRelatedEntityFilterPaths';
import { getRelatedEntityPathKey } from './relatedEntityPath';

/**
 * True when `criteria` carries a cross-workflow filter whose related-card path has no nested
 * options resolved yet, so mapping it to state right now would silently drop that filter.
 *
 * This is the *only* related-options change that should re-apply criteria. Depending on
 * `availableRelatedFilters` / `availableRelatedGroups` directly re-applies it far too often: every
 * related card a filter or group menu expands registers demand for that card's nested options,
 * which gives both maps a new identity (twice - once when demand is registered, once when the
 * template arrives). Re-applying criteria mid-edit throws away whatever the user was building,
 * because a filter that hasn't had a value picked yet exists only in `selectedFilters` and so has
 * no criteria representation to be restored from.
 */
export function hasUnresolvedRelatedFilters(
  criteria: API.CollectionCriteriaFragment | null | undefined,
  availableRelatedFilters: RelatedFilterOptions,
  availableRelatedGroups: RelatedFilterOptions,
): boolean {
  // Absence of a key means "not resolved yet"; a resolved path with no filterable or groupable
  // components gets an empty array, not a missing entry (see `useRelatedFilterOptions`).
  return getRelatedEntityFilterPaths(criteria).some(path => {
    const pathKey = getRelatedEntityPathKey(path);
    return !availableRelatedFilters.has(pathKey) || !availableRelatedGroups.has(pathKey);
  });
}
